import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TemplateEditor = ({ 
  template = null, 
  isOpen = false, 
  onClose, 
  onSave,
  className = '' 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'custom',
    content: '',
    tags: [],
    variables: []
  });
  const [previewData, setPreviewData] = useState({
    Name: 'John Smith',
    Birthday: 'December 25th',
    LastContact: '2 weeks ago',
    Company: 'Tech Solutions Inc'
  });
  const [activeTab, setActiveTab] = useState('editor');

  const categoryOptions = [
    { value: 'birthday', label: 'Birthday' },
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'holiday', label: 'Holiday' },
    { value: 'custom', label: 'Custom' }
  ];

  const availableVariables = [
    { name: 'Name', description: 'Contact\'s full name' },
    { name: 'FirstName', description: 'Contact\'s first name' },
    { name: 'Birthday', description: 'Contact\'s birthday date' },
    { name: 'LastContact', description: 'Time since last contact' },
    { name: 'Company', description: 'Contact\'s company' },
    { name: 'Age', description: 'Contact\'s age' },
    { name: 'Location', description: 'Contact\'s location' }
  ];

  useEffect(() => {
    if (template) {
      setFormData({
        name: template?.name || '',
        category: template?.category || 'custom',
        content: template?.content || '',
        tags: template?.tags || [],
        variables: template?.variables || []
      });
    } else {
      setFormData({
        name: '',
        category: 'custom',
        content: '',
        tags: [],
        variables: []
      });
    }
  }, [template, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const insertVariable = (variableName) => {
    const variable = `{${variableName}}`;
    const textarea = document.getElementById('template-content');
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const newContent = formData?.content?.substring(0, start) + variable + formData?.content?.substring(end);
    
    handleInputChange('content', newContent);
    
    // Update variables list
    if (!formData?.variables?.includes(variableName)) {
      handleInputChange('variables', [...formData?.variables, variableName]);
    }
  };

  const generatePreview = () => {
    let preview = formData?.content;
    Object.entries(previewData)?.forEach(([key, value]) => {
      const regex = new RegExp(`{${key}}`, 'g');
      preview = preview?.replace(regex, value);
    });
    return preview;
  };

  const handleSave = () => {
    if (!formData?.name?.trim() || !formData?.content?.trim()) {
      return;
    }

    const templateData = {
      ...formData,
      id: template?.id || Date.now()?.toString(),
      lastModified: new Date()?.toISOString(),
      usageCount: template?.usageCount || 0
    };

    onSave(templateData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-500 bg-black bg-opacity-50 flex items-center justify-center p-4 ${className}`}>
      <div className="bg-surface rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">
            {template ? 'Edit Template' : 'Create New Template'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('editor')}
            className={`px-6 py-3 font-medium transition-micro ${
              activeTab === 'editor' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Editor
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-6 py-3 font-medium transition-micro ${
              activeTab === 'preview' ?'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab('variables')}
            className={`px-6 py-3 font-medium transition-micro ${
              activeTab === 'variables'
                ? 'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Variables
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'editor' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Template Name"
                  type="text"
                  placeholder="Enter template name"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  required
                />
                <Select
                  label="Category"
                  options={categoryOptions}
                  value={formData?.category}
                  onChange={(value) => handleInputChange('category', value)}
                />
              </div>

              {/* Content Editor */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Message Content
                </label>
                <div className="border border-border rounded-lg overflow-hidden">
                  {/* Variable Buttons */}
                  <div className="bg-muted p-3 border-b border-border">
                    <div className="flex flex-wrap gap-2">
                      {availableVariables?.map((variable) => (
                        <Button
                          key={variable?.name}
                          variant="outline"
                          size="sm"
                          onClick={() => insertVariable(variable?.name)}
                          className="text-xs"
                        >
                          {`{${variable?.name}}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Text Area */}
                  <textarea
                    id="template-content"
                    className="w-full p-4 min-h-[200px] resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter your message template here. Use the buttons above to insert variables like {Name} or {Birthday}."
                    value={formData?.content}
                    onChange={(e) => handleInputChange('content', e?.target?.value)}
                  />
                </div>
                <p className="text-sm text-text-secondary mt-2">
                  Use variables like {`{Name}`} and {`{Birthday}`} to personalize your messages.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="space-y-6">
              {/* Preview Settings */}
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-4">Preview Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(previewData)?.map(([key, value]) => (
                    <Input
                      key={key}
                      label={key}
                      type="text"
                      value={value}
                      onChange={(e) => setPreviewData(prev => ({
                        ...prev,
                        [key]: e?.target?.value
                      }))}
                    />
                  ))}
                </div>
              </div>

              {/* Preview Output */}
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-4">Message Preview</h3>
                <div className="bg-muted border border-border rounded-lg p-4">
                  <div className="bg-surface rounded-lg p-4 shadow-card">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <Icon name="MessageSquare" size={20} color="white" />
                      </div>
                      <div className="flex-1">
                        <div className="bg-primary text-primary-foreground rounded-lg p-3 max-w-md">
                          <p className="whitespace-pre-wrap">
                            {generatePreview() || 'Your message preview will appear here...'}
                          </p>
                        </div>
                        <p className="text-xs text-text-secondary mt-2">
                          Sent via ContactSync Pro
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'variables' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-text-primary mb-4">Available Variables</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableVariables?.map((variable) => (
                    <div key={variable?.name} className="bg-muted rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <code className="text-primary font-mono text-sm bg-surface px-2 py-1 rounded">
                          {`{${variable?.name}}`}
                        </code>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => insertVariable(variable?.name)}
                        >
                          Insert
                        </Button>
                      </div>
                      <p className="text-sm text-text-secondary">
                        {variable?.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Used Variables */}
              {formData?.variables?.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-text-primary mb-4">Used in This Template</h3>
                  <div className="flex flex-wrap gap-2">
                    {formData?.variables?.map((variable) => (
                      <span
                        key={variable}
                        className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {`{${variable}}`}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            disabled={!formData?.name?.trim() || !formData?.content?.trim()}
          >
            {template ? 'Update Template' : 'Create Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;